import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spin, Tooltip, message } from 'antd';
import {
  CloseOutlined,
  DownloadOutlined,
  FileUnknownOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import { renderAsync } from 'docx-preview';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../styles/DocumentViewer.css';

// pdf.js needs a web worker; Vite resolves this URL at build time so it works
// in dev and production without copying files into /public.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif', 'ico'];
const TEXT_EXT = ['txt', 'csv', 'log', 'json', 'md', 'xml'];
const VIDEO_EXT = ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'];
const ZOOM_STEP = 0.2;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 3;

function fileNameFromUrl(url) {
  if (!url) return 'Document';
  const clean = String(url).split('?')[0].split('#')[0];
  return decodeURIComponent(clean.split('/').pop() || clean) || 'Document';
}

function extOf(nameOrUrl) {
  const clean = String(nameOrUrl || '').split('?')[0].split('#')[0];
  const dot = clean.lastIndexOf('.');
  return dot === -1 ? '' : clean.slice(dot + 1).toLowerCase();
}

// kindOf maps a document to a renderer bucket. An explicit `type` on the doc
// object always wins so callers can override extension-based detection.
function kindOf(doc) {
  if (doc.type) return doc.type;
  const ext = extOf(doc.name || doc.url);
  if (ext === 'pdf') return 'pdf';
  if (ext === 'doc' || ext === 'docx') return 'docx';
  if (IMAGE_EXT.includes(ext)) return 'image';
  if (TEXT_EXT.includes(ext)) return 'text';
  if (VIDEO_EXT.includes(ext)) return 'video';
  return 'unknown';
}

// normalizeDocs accepts either bare URL strings or objects shaped like the
// detail API documents ({ name, location, path, url, type }).
function normalizeDocs(documents) {
  return (documents || [])
    .map((d, i) => {
      if (typeof d === 'string') {
        return { url: d, name: fileNameFromUrl(d), key: String(i) };
      }
      const url = d.url || d.location || d.path || '';
      return {
        url,
        name: d.name || fileNameFromUrl(url),
        type: d.type,
        key: String(d.id ?? d._id ?? i),
      };
    })
    .filter((d) => d.url);
}

/* ------------------------------- renderers ------------------------------- */

function PdfRenderer({ url, scale }) {
  const wrapRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (error) {
    return <FallbackStage label="This PDF could not be displayed." />;
  }

  return (
    <div ref={wrapRef} className="dv-pdf-wrap">
      <Document
        file={url}
        loading={<Spin />}
        error={<FallbackStage label="This PDF could not be displayed." />}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => setError(true)}
      >
        {Array.from({ length: numPages }, (_, i) => (
          <Page
            key={`page-${i + 1}`}
            pageNumber={i + 1}
            width={width ? width * scale : undefined}
            className="dv-pdf-page"
            renderTextLayer
            renderAnnotationLayer
          />
        ))}
      </Document>
    </div>
  );
}

PdfRenderer.propTypes = { url: PropTypes.string.isRequired, scale: PropTypes.number.isRequired };

function DocxRenderer({ url }) {
  const ref = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled || !ref.current) return undefined;
        ref.current.innerHTML = '';
        return renderAsync(blob, ref.current, undefined, {
          className: 'dv-docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
        });
      })
      .then(() => {
        if (!cancelled) setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (status === 'error') {
    return <FallbackStage label="This document could not be displayed." />;
  }

  return (
    <div className="dv-docx-scroll">
      {status === 'loading' && <div className="dv-center"><Spin /></div>}
      <div ref={ref} style={{ visibility: status === 'ready' ? 'visible' : 'hidden' }} />
    </div>
  );
}

DocxRenderer.propTypes = { url: PropTypes.string.isRequired };

function TextRenderer({ url }) {
  const [text, setText] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => {
        if (!cancelled) {
          setText(t);
          setStatus('ready');
        }
      })
      .catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (status === 'loading') return <div className="dv-center"><Spin /></div>;
  if (status === 'error') return <FallbackStage label="This file could not be displayed." />;
  return <pre className="dv-text">{text}</pre>;
}

TextRenderer.propTypes = { url: PropTypes.string.isRequired };

function ImageRenderer({ url, name, scale }) {
  const [error, setError] = useState(false);
  if (error) return <FallbackStage label="This image could not be displayed." />;
  return (
    <div className="dv-image-wrap">
      <img
        className="dv-image"
        src={url}
        alt={name}
        style={{ transform: `scale(${scale})` }}
        onError={() => setError(true)}
      />
    </div>
  );
}

ImageRenderer.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  scale: PropTypes.number.isRequired,
};

function VideoRenderer({ url, name }) {
  const [error, setError] = useState(false);
  if (error) return <FallbackStage label="This video could not be played." />;
  return (
    <div className="dv-video-wrap">
      <video
        className="dv-video"
        src={url}
        title={name}
        controls
        preload="metadata"
        controlsList="nodownload"
        onError={() => setError(true)}
      />
    </div>
  );
}

VideoRenderer.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

function FallbackStage({ label }) {
  return (
    <div className="dv-center dv-fallback">
      <FileUnknownOutlined className="dv-fallback-icon" />
      <p>{label}</p>
      <span className="dv-fallback-hint">Use the download button to open it.</span>
    </div>
  );
}

FallbackStage.propTypes = { label: PropTypes.string.isRequired };

/* ----------------------------- main component ---------------------------- */

// ViewerBody holds the per-session state (active index + zoom). It lives inside
// the Modal body, which is destroyed on close (destroyOnHidden), so it remounts
// fresh on every open — no manual "reset on open" effects required.
function ViewerBody({ docs, initialIndex, onClose }) {
  const total = docs.length;
  const safeInitial = Math.min(Math.max(initialIndex, 0), Math.max(0, total - 1));
  const [index, setIndex] = useState(safeInitial);
  const [scale, setScale] = useState(1);

  const current = docs[index];
  const kind = current ? kindOf(current) : 'unknown';
  const zoomable = kind === 'pdf' || kind === 'image';

  // setActive changes the document and resets zoom in one event handler, so we
  // never have to reset zoom from an effect.
  const setActive = useCallback((next) => {
    setIndex(next);
    setScale(1);
  }, []);

  const goPrev = useCallback(() => setActive(Math.max(0, index - 1)), [index, setActive]);
  const goNext = useCallback(
    () => setActive(Math.min(total - 1, index + 1)),
    [index, total, setActive],
  );

  // Keyboard navigation.
  useEffect(() => {
    const onKey = (e) => {
      // A focused <video> uses arrow keys to seek — don't switch documents.
      if (e.target?.tagName === 'VIDEO') return;
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  const download = useCallback(async (doc) => {
    if (!doc) return;
    try {
      const res = await fetch(doc.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      // Cross-origin without CORS: fall back to opening in a new tab.
      message.info('Opening file in a new tab…');
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  function renderStage() {
    if (!current) {
      return <FallbackStage label="No document to preview." />;
    }
    switch (kind) {
      case 'pdf':
        return <PdfRenderer key={current.key} url={current.url} scale={scale} />;
      case 'docx':
        return <DocxRenderer key={current.key} url={current.url} />;
      case 'image':
        return (
          <ImageRenderer key={current.key} url={current.url} name={current.name} scale={scale} />
        );
      case 'text':
        return <TextRenderer key={current.key} url={current.url} />;
      case 'video':
        return <VideoRenderer key={current.key} url={current.url} name={current.name} />;
      default:
        return <FallbackStage label="Preview is not available for this file type." />;
    }
  }

  return (
    <div className="dv-root">
      {/* toolbar */}
        <div className="dv-toolbar">
          <div className="dv-title" title={current?.name}>
            <span className="dv-title-text">{current?.name || 'Document'}</span>
            {total > 1 && <span className="dv-counter">{index + 1} / {total}</span>}
          </div>
          <div className="dv-actions">
            {zoomable && (
              <>
                <Tooltip title="Zoom out">
                  <button
                    type="button"
                    className="dv-icon-btn"
                    onClick={() => setScale((s) => Math.max(ZOOM_MIN, +(s - ZOOM_STEP).toFixed(2)))}
                    disabled={scale <= ZOOM_MIN}
                  >
                    <ZoomOutOutlined />
                  </button>
                </Tooltip>
                <span className="dv-zoom-label">{Math.round(scale * 100)}%</span>
                <Tooltip title="Zoom in">
                  <button
                    type="button"
                    className="dv-icon-btn"
                    onClick={() => setScale((s) => Math.min(ZOOM_MAX, +(s + ZOOM_STEP).toFixed(2)))}
                    disabled={scale >= ZOOM_MAX}
                  >
                    <ZoomInOutlined />
                  </button>
                </Tooltip>
                <span className="dv-divider" />
              </>
            )}
            <Tooltip title="Download">
              <button type="button" className="dv-icon-btn dv-download" onClick={() => download(current)}>
                <DownloadOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Close">
              <button type="button" className="dv-icon-btn" onClick={onClose}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* stage + slider arrows */}
        <div className="dv-stage">
          {total > 1 && (
            <button
              type="button"
              className="dv-nav dv-nav--prev"
              onClick={goPrev}
              disabled={index === 0}
              aria-label="Previous document"
            >
              <LeftOutlined />
            </button>
          )}

          <div className="dv-canvas">{renderStage()}</div>

          {total > 1 && (
            <button
              type="button"
              className="dv-nav dv-nav--next"
              onClick={goNext}
              disabled={index === total - 1}
              aria-label="Next document"
            >
              <RightOutlined />
            </button>
          )}
        </div>

        {/* dots */}
        {total > 1 && (
          <div className="dv-dots">
            {docs.map((d, i) => (
              <button
                key={d.key}
                type="button"
                className={`dv-dot${i === index ? ' dv-dot--active' : ''}`}
                onClick={() => setActive(i)}
                aria-label={`Go to document ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
  );
}

ViewerBody.propTypes = {
  docs: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialIndex: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

// DocumentViewer is the public component: a Modal shell that mounts ViewerBody
// only while open. `documents` may be URL strings or document objects.
//
// Pass `inline` to render ViewerBody directly with no Modal — used by the timesheet
// manager review, which shows the uploaded screenshot side-by-side with the calendar
// rather than in a popup. Everything ViewerBody already does (pdf/image/docx render,
// zoom, download, multi-document navigation) comes along for free.
export default function DocumentViewer({ documents, open, onClose, initialIndex = 0, inline = false }) {
  const docs = useMemo(() => normalizeDocs(documents), [documents]);

  if (inline) {
    return (
      <div className="dv-root--inline">
        <ViewerBody docs={docs} initialIndex={initialIndex} onClose={onClose ?? (() => {})} />
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      closable={false}
      centered
      width="min(1100px, 94vw)"
      className="dv-modal"
      styles={{ content: { padding: 0, overflow: 'hidden', borderRadius: 14 }, body: { padding: 0 } }}
      destroyOnHidden
    >
      <ViewerBody docs={docs} initialIndex={initialIndex} onClose={onClose} />
    </Modal>
  );
}

DocumentViewer.propTypes = {
  // Array of URL strings or document objects ({ url|location|path, name?, type?, id? }).
  documents: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ).isRequired,
  // Required for the Modal shell; unused (and optional) when `inline` is set.
  open: PropTypes.bool,
  onClose: PropTypes.func,
  initialIndex: PropTypes.number,
  // Render the viewer body directly, with no Modal wrapper.
  inline: PropTypes.bool,
};
